import type { ReactNode } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Circle, Info } from 'lucide-react'
import { useAuth } from '../../core/auth'
import type { NtCourse, NtPlacement } from '../../core/types'
import { useNorseTerm, type NorseTermRoles } from './NorseTermData'
import { WaitingOnYou, useSteps, type Step } from './ChecklistView'
import { stepHref } from './checklist'
import { MyCourse } from './MyCourse'
import { Catalog } from './Catalog'
import { ReviewQueue } from './ReviewQueue'
import { Blocks } from './Blocks'
import { Students } from './Students'
import { BuildWeek } from './BuildWeek'
import { Attendance, MyRoster } from './Attendance'
import { Checkins } from './Checkins'
import { Projects, SeminarQueue, StudentProject, ProjectExamples } from './Projects'
import { StudentSchedule } from './StudentSchedule'
import { ArtifactsOffice } from './ArtifactsOffice'
import { Visits } from './Visits'
import { Awards } from './Awards'
import { History } from './History'
import { TermSettings } from './TermSettings'
import { Exemplars } from './Exemplars'
import { Dashboard } from './PrepDashboard'
import { CourseExemplarsAdmin, CourseExemplarPanel } from './CourseExemplars'
import { AttendanceReport } from './AttendanceReport'
import { NorseTermInfo } from './Overview'
import { Loading, ErrorState, Denied, Empty } from '../../ui/States'
import { CourseSelections } from './CourseSelections'
import { RosterTab } from '../admin/RosterTab'
import { RosterUpload } from './RosterUpload'
import { NorseTermStaff } from './NorseTermStaff'
import { CreditRecovery } from './CreditRecovery'
import { TermBanner, NextUp } from './TermBanner'
import { OpenHouse } from './OpenHouse'
import { Email } from './Email'
import { classYearFromEmail, gradeFor } from '../../core/students'

/**
 * /norseterm and /norseterm/<page>.
 *
 * The home is a numbered list of *your* steps, nothing else: a step shows
 * whether it is done and opens its own page, and the one you are on is
 * highlighted with a Continue button. Every task lives on a page of its own
 * with a way back to the list, rather than as one of a dozen tabs under a
 * phase bar; that screen tried to be everything at once and nobody could tell
 * where to start. The date decides which steps show (before the week, during
 * it, the open house), so there is no phase menu to pick from.
 *
 * Steps come from checklist.ts, derived from the data, so they tick
 * themselves. Things that are not steps (the catalog, examples, history,
 * setup) are one short "More" list under them.
 *
 * Old links (/norseterm?phase=…&tab=X) still work: they redirect to
 * /norseterm/X.
 */

export function NorseTerm() {
  const { student } = useAuth()
  return student ? <StudentNorseTerm /> : <StaffHome />
}

/* ---------------- pages ---------------- */

interface PageDef {
  title: string
  /** Who may open it, from their NorseTerm roles. */
  show: (r: NorseTermRoles) => boolean
  /** Listed under More on the home page, in this group. Omitted = reached only from a step. */
  more?: 'look' | 'manage'
}

export const PAGES: Record<string, PageDef> = {
  info:            { title: 'About Norse Term', show: () => true, more: 'look' },
  mine:            { title: 'My course', show: (r) => r.teach },
  examples:        { title: 'Example courses', show: () => true, more: 'look' },
  catalog:         { title: 'Course catalog', show: () => true, more: 'look' },
  review:          { title: 'Review course proposals', show: (r) => r.review },
  seminar:         { title: 'My seminar', show: (r) => r.seminar },
  projects:        { title: 'Independent project proposals', show: (r) => r.run || r.review || r.attend, more: 'manage' },
  dashboard:       { title: 'Progress dashboard', show: (r) => r.review || r.run },
  'staff-progress':   { title: 'Staff: course completion', show: (r) => r.review || r.run, more: 'manage' },
  'student-progress': { title: 'Students: course selections and project proposals', show: (r) => r.review || r.run, more: 'manage' },
  students:        { title: 'Student list and course selections', show: (r) => r.run },
  roster:          { title: 'Student list', show: (r) => r.run || r.attend, more: 'manage' },
  staff:           { title: 'Norse Term staff and roles', show: (r) => !!r.grant, more: 'manage' },
  blocks:          { title: 'Put courses in blocks', show: (r) => r.run },
  build:           { title: 'Run placement', show: (r) => r.run },
  credit:          { title: 'Credit recovery', show: (r) => r.run, more: 'manage' },
  artifacts:       { title: 'Artifact courses', show: (r) => r.run || r.attend, more: 'manage' },
  attend:          { title: 'Take attendance', show: (r) => r.teach },
  myroster:        { title: 'Your roster', show: (r) => r.teach },
  checkins:        { title: 'Project check-ins', show: (r) => r.teach || r.review || r.run || r.attend, more: 'look' },
  attendance:      { title: 'Attendance', show: (r) => r.attend, more: 'manage' },
  rosters:         { title: 'Course rosters', show: (r) => r.run || r.attend, more: 'manage' },
  visits:          { title: 'Site visits', show: () => true, more: 'look' },
  openhouse:       { title: 'Open house checklist', show: (r) => r.run || r.attend || r.review, more: 'manage' },
  awards:          { title: 'Open-house awards', show: () => true, more: 'look' },
  past:            { title: 'Past Norse Terms', show: () => true, more: 'look' },
  settings:        { title: 'Dates and rules', show: (r) => r.configure, more: 'manage' },
  courseExemplars: { title: 'Edit example courses', show: (r) => r.configure, more: 'manage' },
  exemplars:       { title: 'Edit example projects', show: (r) => r.configure, more: 'manage' },
  email:           { title: 'Email', show: (r) => r.run || r.configure, more: 'manage' },
}

/** One task, on a page of its own: back to your steps, the task, nothing else. */
export function NorseTermPage() {
  const { page = '' } = useParams()
  const { student, user, profile } = useAuth()
  const nt = useNorseTerm()
  const { term, loading, roles, courses, coursesErr, myCourses, rosters, students, proposals, placement } = nt
  const def = PAGES[page]

  if (student) return <StudentPage page={page} />
  if (!def) return <Navigate to="/norseterm" replace />
  if (loading) return <Loading label="Loading Norse Term" />
  if (!def.show(roles)) return <Denied what={def.title} />
  // About Norse Term is a page in its own right, with its own heading.
  if (page === 'info') return <NorseTermInfo />
  if (coursesErr) return <ErrorState body={`Could not load courses. ${coursesErr}`} onRetry={() => location.reload()} />
  if (!courses) return <Loading label="Loading courses" />

  const who = profile?.displayName ?? user?.email ?? ''
  const me = { email: (user?.email ?? '').toLowerCase(), name: who }
  const { run, review, configure } = roles

  const body: Record<string, () => ReactNode> = {
    mine: () => <MyCourse term={term} mine={myCourses} rosters={rosters} uid={user!.uid} name={who} email={me.email} />,
    examples: () => <CourseExemplarPanel defaultOpen />,
    catalog: () => <Catalog courses={courses} />,
    review: () => <ReviewQueue courses={courses} who={who} />,
    seminar: () => <SeminarQueue termId={term.id} me={me} />,
    projects: () => <Projects proposals={proposals} canDecide={run || review} me={me} />,
    dashboard: () => <Dashboard term={term} />,
    'staff-progress': () => <Dashboard term={term} show="staff" />,
    'student-progress': () => <Dashboard term={term} show="students" />,
    students: () => <Students term={term} courses={courses} students={students} />,
    blocks: () => <Blocks term={term} courses={courses} />,
    build: () => <BuildWeek term={term} courses={courses} students={students}
      proposals={proposals} placement={placement} who={who} />,
    artifacts: () => <ArtifactsOffice who={who} />,
    attend: () => <Attendance term={term} rosters={rosters} courses={courses} who={who} />,
    myroster: () => <MyRoster rosters={rosters} courses={courses} />,
    checkins: () => <Checkins />,
    attendance: () => <AttendanceReport term={term} />,
    rosters: () => <AllRosters courses={courses} placement={placement} />,
    visits: () => <Visits term={term} courses={courses} canAssign={run} meEmail={me.email} />,
    awards: () => <Awards term={term} courses={courses} canRecord={run || configure} who={who} />,
    past: () => <History current={term} canSeeStudents={run} />,
    settings: () => <TermSettings term={term} />,
    courseExemplars: () => <CourseExemplarsAdmin who={who} />,
    exemplars: () => <Exemplars who={who} />,
    roster: () => <>{run && <RosterUpload />}<RosterTab canEdit /></>,
    staff: () => <NorseTermStaff />,
    credit: () => <CreditRecovery />,
    openhouse: () => <OpenHouse />,
    email: () => <Email />,
  }

  return (
    <>
      <Link to="/norseterm" className="backlink">← My Norse Term steps</Link>
      <div className="pagehead">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{def.title}</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '2px 0 0' }}>Norse Term · {term.weekLabel}</p>
        </div>
      </div>
      {body[page]?.()}
    </>
  )
}

/* ---------------- the home: your steps ---------------- */

function StaffHome() {
  const { loading, roles, phase } = useNorseTerm()
  const { sections, later, current } = useSteps()
  const [params] = useSearchParams()

  // Old links: /norseterm?phase=prep&tab=review&course=… → /norseterm/review?course=…
  const oldTab = params.get('tab')
  if (oldTab && PAGES[oldTab]) {
    const rest = new URLSearchParams(params)
    rest.delete('tab'); rest.delete('phase')
    const q = rest.toString()
    return <Navigate to={`/norseterm/${oldTab}${q ? `?${q}` : ''}`} replace />
  }
  if (loading) return <Loading label="Loading Norse Term" />

  const all = sections.flatMap((x) => x.items)
  const done = all.filter((i) => i.done).length
  const total = all.filter((i) => i.done !== null).length

  return (
    <>
      <TermBanner title />
      <NextUp />

      {phase === 'during' && <WaitingOnYou />}

      {sections.length === 0 ? (
        <section className="card" style={{ marginTop: 'var(--s-3)' }}>
          <h2 className="ov__h">{phase === 'hist' ? 'This Norse Term is over' : 'Nothing for you to do yet'}</h2>
          <p className="ov__p" style={{ margin: 0 }}>
            {phase === 'hist'
              ? 'Rosters, attendance and awards are under Past Norse Terms below.'
              : 'Look around below; your steps will appear here when there is something to do.'}
          </p>
        </section>
      ) : (
        <>
          {total > 0 && <p className="steps__count">{done} of {total} steps done</p>}
          {sections.map((sec) => (
            <section key={sec.key} className="card steps" aria-label={sec.title}>
              <div className="steps__head">
                <h2 className="ov__h" style={{ margin: 0 }}>{sec.title}</h2>
                {sec.note && <span className="work__sub">{sec.note}</span>}
              </div>
              <ol className="steps__list">
                {sec.items.map((i, n) => (
                  <StepRow key={i.role + i.id} step={i} n={n + 1} current={i === current} />
                ))}
              </ol>
            </section>
          ))}
        </>
      )}

      {later.length > 0 && (
        <p className="steps__later">
          <strong>Later, in March:</strong>{' '}
          {[...new Set(later.map((i) => i.label))].join(' · ')}
        </p>
      )}

      <More roles={roles} />
    </>
  )
}

function StepRow({ step, n, current }: { step: Step; n: number; current: boolean }) {
  const to = stepHref(step)
  return (
    <li className={`steps__item${step.done ? ' is-done' : ''}${current ? ' is-current' : ''}`}>
      <span className="steps__mark" aria-hidden="true">
        {step.done === null ? <Info size={18} />
          : step.done ? <CheckCircle2 size={20} />
          : current ? <span className="steps__num">{n}</span>
          : <Circle size={18} />}
      </span>
      <div className="steps__main">
        <Link to={to} className="steps__label">
          {step.label}
          <span className="visually-hidden">
            {step.done === null ? '' : step.done ? ' (done)' : current ? ' (your next step)' : ' (to do)'}
          </span>
        </Link>
        {step.detail && <div className="steps__detail">{step.detail}</div>}
      </div>
    </li>
  )
}

/** Everything that is not a step: looking things up, and (for admins) managing. */
function More({ roles }: { roles: NorseTermRoles }) {
  const list = (g: 'look' | 'manage') => Object.entries(PAGES).filter(([, p]) => p.more === g && p.show(roles))
  const manage = list('manage')
  const look = list('look')
  return (
    <section className="more" aria-label="More">
      {manage.length > 0 && (
        <div>
          <div className="more__head">Manage</div>
          <ul className="more__list">
            {manage.map(([k, p]) => <li key={k}><Link to={`/norseterm/${k}`}>{p.title}</Link></li>)}
          </ul>
        </div>
      )}
      <div>
        <div className="more__head">Look things up</div>
        <ul className="more__list">
          {look.map(([k, p]) => <li key={k}><Link to={`/norseterm/${k}`}>{p.title}</Link></li>)}
        </ul>
      </div>
    </section>
  )
}

/* ---------------- students ---------------- */

/**
 * A student's NorseTerm: the same numbered steps as staff. Course selections
 * (everyone) and an independent project proposal (10th to 12th grade,
 * optional) are separate pages, because they are separate decisions: every
 * student ranks courses in case a proposal is not approved.
 */
function StudentNorseTerm() {
  const { student } = useAuth()
  const { loading, phase } = useNorseTerm()
  const { sections, current } = useSteps()
  if (loading || !student) return <Loading label="Loading Norse Term" />
  const year = classYearFromEmail(student.email)
  const freshman = year ? gradeFor(year) <= 9 : false
  return (
    <>
      <TermBanner title />
      <NextUp />
      {sections.map((sec) => (
        <section key={sec.key} className="card steps" aria-label={sec.title}>
          <div className="steps__head"><h2 className="ov__h" style={{ margin: 0 }}>Your steps</h2></div>
          <ol className="steps__list">
            {sec.items.map((i, n) => <StepRow key={i.id} step={i} n={n + 1} current={i === current} />)}
          </ol>
        </section>
      ))}
      {phase === 'prep' && (
        <p className="steps__later">
          {freshman
            ? <>As a freshman, you&apos;ll take two on-site courses, one in the morning and one in the afternoon. Independent project proposals open up in 10th grade.</>
            : <>Everyone makes course selections. An independent project proposal is optional, for students with a host lined up; if it isn&apos;t approved, you&apos;ll be placed from your course selections.</>}
        </p>
      )}
    </>
  )
}

const STUDENT_PAGES: Record<string, string> = {
  selections: 'Course selections',
  project: 'Independent project proposal',
  schedule: 'My Norse Term week',
  examples: 'Example projects',
}

function StudentPage({ page }: { page: string }) {
  const { student } = useAuth()
  const { term, loading, mySchedule } = useNorseTerm()
  const title = STUDENT_PAGES[page]
  if (!title) return <Navigate to="/norseterm" replace />
  if (loading || !student) return <Loading label="Loading Norse Term" />
  const year = classYearFromEmail(student.email)
  const freshman = year ? gradeFor(year) <= 9 : false
  return (
    <>
      <Link to="/norseterm" className="backlink">← My Norse Term steps</Link>
      <div className="pagehead">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{title}</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '2px 0 0' }}>Norse Term · {term.weekLabel}</p>
        </div>
      </div>
      {page === 'selections' && <CourseSelections me={student} />}
      {page === 'examples' && <ProjectExamples />}
      {page === 'project' && (freshman
        ? <Empty title="Not in 9th grade" body="Freshmen take on-site courses. Independent project proposals open up in 10th grade; for now, make your course selections." />
        : (
          <>
            <div className="notice notice--info" style={{ marginTop: 'var(--s-2)' }}>
              <span aria-hidden="true">i</span>
              <div>
                Optional, and separate from your course selections. <strong>You still need to make your course
                selections</strong>: if this proposal isn&apos;t approved, that&apos;s where you&apos;ll be placed.{' '}
                <Link to="/norseterm/selections">Go to course selections →</Link>
              </div>
            </div>
            <StudentProject term={term} me={student} />
          </>
        ))}
      {page === 'schedule' && (
        <StudentSchedule term={term} schedule={mySchedule} me={{ email: student.email, name: student.displayName }} />
      )}
    </>
  )
}

/* ---------------- helpers ---------------- */

/** Seats per course after the last run — the office's version of a roster. */
function AllRosters({ courses, placement }: { courses: NtCourse[]; placement: NtPlacement | null }) {
  if (!placement) {
    return (
      <p className="muted-note">
        Nothing placed yet. Once admin runs placement, rosters appear here and on each teacher&apos;s own screen.
      </p>
    )
  }
  const live = courses.filter((c) => c.status === 'approved' && c.block)
  return (
    <ul className="work" style={{ marginTop: 'var(--s-3)' }}>
      {live.map((c) => {
        const inIt = placement.rows.filter((r) =>
          (c.block === 'AM' ? r.amCourseId : r.pmCourseId) === c.id)
        return (
          <li key={c.id} className="work__row">
            <div className="work__main">
              <div style={{ fontWeight: 610 }}>{c.title}</div>
              <div className="work__sub">{c.block} · {c.teacherName} · {c.room || 'no room'}</div>
              <div className="work__status">
                {inIt.slice(0, 5).map((r) => r.name).join(', ')}
                {inIt.length > 5 && `, +${inIt.length - 5} more`}
              </div>
            </div>
            <span className={`pill ${inIt.length < c.min ? 'pill--serious' : 'pill--good'}`}>
              {inIt.length} / {c.cap}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
